import React, { useCallback, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import "./Modal.scss";
import { ModalContext } from "./ModalContext";
import ClassNames from "classnames";
import { IconButton } from "../../controls";

export const MODAL_CONTAINER_CLASS = `modal-dialog-container`;
export const MODAL_ROOT_ID = "modal-root";

const ESC_KEY = "Escape";

type ModalSize = "xxs" | "xs" | "s" | "m" | "l" | "xl" | "xxl" | "xxxl";

// Runtime shape of the modal config object passed to `openModal` and stored as
// `modalContent`. NOTE: ModalContext declares `modalContent` as React.ReactNode,
// but at runtime it is this config object (see useModal.ts / openModal callers) —
// the declared context type is stale. See report flag.
export interface ModalContent {
  title?: string;
  icon?: string;
  img?: string;
  component?: React.ReactNode;
  size?: ModalSize;
  className?: string;
  modalRight?: boolean;
  modalLeft?: boolean;
  overlay?: boolean;
  image?: string;
  overlayClass?: string;
  displayHeader?: boolean;
  closeOnEsc?: boolean;
}

export const Modal: React.FC = () => {
  const { isOpen, modalContent, closeModal, showCloseButton } =
    useContext(ModalContext);
  // `modalContent` is declared as ReactNode on ModalContext but is the config
  // object at runtime; cast to the true runtime shape.
  const {
    title,
    icon = "",
    img,
    component,
    size = "xs",
    className = "",
    modalRight = false,
    modalLeft = false,
    overlay = true,
    image,
    overlayClass = "",
    displayHeader = true,
    closeOnEsc = true,
  } = (modalContent ?? {}) as unknown as ModalContent;

  const classNameWrapper = ClassNames(
    "modal-dialog-content",
    {
      "modal-right": modalRight,
      "modal-left": modalLeft,
      "modal-size-xxs": size === "xxs",
      "modal-size-xs": size === "xs",
      "modal-size-s": size === "s",
      "modal-size-m": size === "m",
      "modal-size-l": size === "l",
      "modal-size-xl": size === "xl",
      "modal-size-xxl": size === "xxl",
      "modal-size-xxxl": size === "xxxl",
    },
    className
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ESC_KEY) {
        event.stopPropagation();
        closeModal();
      }
    },
    [closeModal]
  );

  useEffect(() => {
    if (isOpen && closeOnEsc) {
      document?.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document?.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeOnEsc, handleKeyDown]);

  if (isOpen) {
    return createPortal(
      <div
        className={`${MODAL_CONTAINER_CLASS} ${className ? `${className}-modal-wrapper` : ""}`}
      >
        {/*TODO*/}
        {/*FIX TO HIDE OVERLAY IF SET TO FALSE*/}
        {overlay === false ? null : (
          <div
            className={`modal-dialog-overlay ${
              overlayClass ? overlayClass : ""
            }`}
          />
        )}

        <div className={classNameWrapper}>
          {displayHeader && (
            <div className={`modal-header`}>
              <div className={`modal-header-title-wrap`}>
                {image ? (
                  <div
                    className={`modal-title-image`}
                    style={{ backgroundImage: `url(${image})` }}
                  />
                ) : (
                  <></>
                )}

                {icon ? <i className={`seem-icon ${icon}`} /> : null}
                {img ? <img className={"modal-img"} src={img} /> : null}
                <h3 className={`modal-title`}>{title}</h3>
              </div>

              {showCloseButton && (
                <div className={`modal-dialog-actions`}>
                  <IconButton
                    isIcon="seem-icon-close"
                    type={`button`}
                    withTooltip={{ content: "Close" }}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      closeModal();
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <div className={`modal-content`}>{component}</div>
        </div>
      </div>,
      document.querySelector(`#${MODAL_ROOT_ID}`) as Element
    );
  } else {
    return null;
  }
};
